import { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { ArrowLeft, CheckCircle2, Lock } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) return setErr("Password must be at least 8 characters.");
    if (pw !== pw2) return setErr("Passwords don't match.");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/login"), 2000);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white border border-slate-200 p-8 shadow-sm rounded-sm" data-testid="reset-password-card">
        <Link to="/login" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 text-sm mb-6 transition-colors" data-testid="reset-back-login">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>
        <div className="overline text-slate-500 mb-2">Set a new password</div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{fontFamily:"Outfit"}}>
          {done ? "Password updated." : "Choose a new password."}
        </h1>
        <p className="mt-2 text-sm text-slate-500">
          {done
            ? "Redirecting you to sign in…"
            : "Enter a new password for your account. Must be at least 8 characters."}
        </p>

        {!token && !done ? (
          <div className="mt-8 text-sm text-red-600" data-testid="reset-missing-token">
            This link is missing a reset token. Please request a new reset email.
          </div>
        ) : !done ? (
          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            <div>
              <label className="overline text-slate-600">New password</label>
              <input
                type="password"
                data-testid="reset-password-input"
                className="w-full mt-2 input-clean"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                required
                minLength={8}
                placeholder="••••••••"
              />
            </div>
            <div>
              <label className="overline text-slate-600">Confirm password</label>
              <input
                type="password"
                data-testid="reset-password-confirm-input"
                className="w-full mt-2 input-clean"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                required
                minLength={8}
                placeholder="••••••••"
              />
            </div>
            {err && <div className="text-sm text-red-600" data-testid="reset-error">{err}</div>}
            <button
              type="submit"
              disabled={busy}
              data-testid="reset-submit-button"
              className="w-full btn-primary-dark flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {busy ? "Updating…" : (<><Lock className="w-4 h-4" /> Update password</>)}
            </button>
          </form>
        ) : (
          <div className="mt-8 flex items-start gap-3 bg-emerald-50 border border-emerald-200 p-4 rounded-sm" data-testid="reset-success">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
            <div className="text-sm text-emerald-900">Your password has been updated.</div>
          </div>
        )}
      </div>
    </div>
  );
}
